import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, Button, CircularProgress, Typography, Box, IconButton } from '@mui/material';
import { Close, CheckCircle } from '@mui/icons-material';
import { useWhatsApp } from '../../../marketplace/hooks/useWhatsApp';

const QR_CODE_EXPIRY_SECONDS = 60;

interface WhatsAppQRCodeModalProps {
  open: boolean;
  onClose: () => void;
  marketplaceUuid: string;
}

export const WhatsAppQRCodeModal: React.FC<WhatsAppQRCodeModalProps> = ({ open, onClose, marketplaceUuid }) => {
  const [qrCode, setQrCode] = useState<string | undefined>();
  const [status, setStatus] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(QR_CODE_EXPIRY_SECONDS);
  const { gerarQRCode, consultarStatus, loading } = useWhatsApp();

  // Check connection status every 2 seconds
  useEffect(() => {
    if (!open || !marketplaceUuid) return;

    const checkStatus = async () => {
      const statusAtual = await consultarStatus(marketplaceUuid);
      setStatus(statusAtual ?? false);

      // Close modal automatically when connected
      if (statusAtual) {
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 2000);

    return () => clearInterval(interval);
  }, [open, marketplaceUuid]); // Removed consultarStatus and onClose from dependencies to prevent infinite loop

  // Refresh QR Code every 60 seconds while not connected
  useEffect(() => {
    if (!open || !marketplaceUuid || status) return;

    const fetchQRCode = async () => {
      const qrCodeBase64 = await gerarQRCode(marketplaceUuid);
      setQrCode(qrCodeBase64);
      setCountdown(QR_CODE_EXPIRY_SECONDS);
    };

    fetchQRCode();
    const interval = setInterval(fetchQRCode, QR_CODE_EXPIRY_SECONDS * 1000);

    return () => clearInterval(interval);
  }, [open, marketplaceUuid, status]); // Removed gerarQRCode from dependencies to prevent infinite loop

  // Countdown timer
  useEffect(() => {
    if (!open || !qrCode || status) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) return QR_CODE_EXPIRY_SECONDS;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, qrCode, status]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setQrCode(undefined);
      setStatus(false);
      setCountdown(QR_CODE_EXPIRY_SECONDS);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="whatsapp-qr-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="whatsapp-qr-dialog-title">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Conectar WhatsApp</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {status ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4
          }}>
            <CheckCircle sx={{ fontSize: 60, mb: 2, color: 'success.main' }} />
            <Typography variant="h6" color="success.main" fontWeight="bold">
              Número conectado com sucesso
            </Typography>
            <Typography variant="body1" align="center" sx={{ mt: 1, color: 'text.secondary' }}>
              Agora seu WhatsApp está pronto para automatizar o seu atendimento.
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 3
          }}>
            <Typography sx={{ mb: 2 }} variant="body1" align="center">
              Leia o QR Code através do WhatsApp no seu celular
            </Typography>
            {loading || !qrCode ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, width: 300 }}>
                <CircularProgress size={60} />
              </Box>
            ) : (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider'
              }}>
                <img
                  src={qrCode}
                  alt="WhatsApp QR Code"
                  style={{ width: "300px", height: "300px", display: 'block' }}
                />
              </Box>
            )}
            {qrCode && !loading && (
              <Typography variant="caption" sx={{ mt: 2, color: 'text.secondary' }} align="center">
                Novo QR Code em <strong>{countdown}s</strong>
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

